// GameState construction (DESIGN §9.2) and field generation.
import { dateToDay } from './time.js';
import { nextFloat, range, normal, subRng } from './rng.js';
import { initMarket } from '../economy/market.js';
import { FIELDS, REGIONS, findDef, isBarrelDef, rigStats, fieldRegionId, fieldTransports, eqDef } from '../sim/defs.js';
import { makeCrew, refreshHirePool } from '../sim/crew.js';
import { newWallet } from '../economy/tokens.js';
import { newMonth } from '../sim/finance.js';
import { nextId } from '../sim/util.js';
import { newResearch } from '../sim/research.js';
import { newSafety } from '../sim/safety.js';
import { initRefining } from '../sim/refining.js';
import { makePlot } from '../sim/shapes.js';
import { quickStart1901, quickStart1946, quickStart1973 } from '../sim/quickstart.js';
import { quickStart2000, quickStart2014 } from '../sim/m4/worldQuickStart.js'; // M4-C

export const SAVE_VERSION = 1;
export const START_DAY = dateToDay(1859, 8, 1);

export function newGame({ seed, companyName, startYear = 1859 } = {}) {
  const s = (seed ?? ((Date.now() ^ 0x5bd1e995) >>> 0)) >>> 0;
  const state = {
    version: SAVE_VERSION, seed: s, rng: s,
    day: START_DAY, speed: 1, paused: false, eraId: 'pioneer',
    company: {
      name: companyName || 'Seneca Rock Oil', cash: 2000, reputation: 30, loans: [],
      negCashDays: 0, accidentFreeSince: START_DAY, wageMult: {},
      stats: { wellsDrilled: 0, dryHoles: 0, dryStreak: 0, ftDrilled: 0, bblProduced: 0, bblSold: 0, bblSpilled: 0, deaths: 0, injuries: 0, gushers: 0, strikes: 0, torpedoes: 0, incidents: 0, revenueTotal: 0, peakNetWorth2025: 0,
        bopSaves: 0, kicks: 0, blowouts: 0, twistOffs: 0, wasteBbl: 0, wasteFines: 0 },
      netWorthHistory: [],
    },
    fields: {}, wells: {}, rigs: {}, items: {}, stock: {}, crew: {}, hirePool: [],
    market: null,
    research: newResearch(),
    safety: newSafety(), memorial: [], decisionQueue: [],
    tokens: newWallet(),
    timers: {},
    regions: {},
    pendingDecision: null, incidents: {},
    finance: { current: null, history: [] },
    news: [], log: [], newsFired: {},
    achievements: {}, goals: {},
    flags: { prorationing: false, standardOilDiscount: false, eraSummaryShown: {} },
    settings: { autoPause: { strike: true, dry: true, gusher: true, blowout: true, injury: true, bitWorn: true, storageFull: true, era: true, news: true, attack: true, problem: true }, units: 'imperial', autosave: true },
    gameOver: null,
    nextId: 1,
  };
  state.finance.current = newMonth(state.day);
  for (const rid of Object.keys(REGIONS)) state.regions[rid] = newRegionState();
  for (const fdef of Object.values(FIELDS)) state.fields[fdef.id] = genField(state, fdef);
  initMarket(state);
  initRefining(state);

  // Starter kit: spring pole rig, a chisel bit, rope, drive pipe, a few barrels.
  const rigDef = findDef('rig', (d) => !rigStats(d).needsEngine) ?? findDef('rig');
  const rig = addRig(state, rigDef.id, 'Rig 1');
  rig.tours = 2;
  const bitDef = findDef('bit', (d) => !d.rigTypes || d.rigTypes.includes(rigStats(rigDef).rigType));
  if (bitDef) { const b = addItem(state, bitDef.id, { type: 'rig', id: rig.uid }); rig.bitUid = b.uid; }
  const ropeDef = findDef('line');
  if (ropeDef) addStock(state, ropeDef.id, 300);
  const pipeDef = findDef('casing', (d) => !d.stats?.perWell);
  if (pipeDef) addStock(state, pipeDef.id, 80);
  const barrel = findDef('storage', isBarrelDef);
  if (barrel) addStock(state, barrel.id, 20);

  const d = makeCrew(state, 'driller', 48, { traits: ['old_hand'] });
  const l = makeCrew(state, 'laborer', 40);
  for (const c of [d, l]) { c.hiredDay = state.day; c.rigId = rig.uid; state.crew[c.id] = c; rig.crewIds.push(c.id); }
  refreshHirePool(state);
  const sy = Number(startYear);
  if (sy >= 2014) quickStart2014(state); // M4-C Quick Start: 20 Jun 2014, a hedged shale independent at the top of the cycle
  else if (sy >= 2000) quickStart2000(state); // M4-C Quick Start: 3 Jan 2000, Y2K independent
  else if (sy >= 1973) quickStart1973(state); // M3 ops Quick Start: 16 Oct 1973, offshore & crisis
  else if (sy >= 1946) quickStart1946(state); // M3 ops Quick Start: 2 Jan 1946, postwar independent
  else if (sy >= 1901) quickStart1901(state); // DESIGN §7 Quick Start: straight into the rotary era
  state.startYear = sy >= 2014 ? 2014 : sy >= 2000 ? 2000 : sy >= 1973 ? 1973 : sy >= 1946 ? 1946 : sy >= 1901 ? 1901 : 1859; // M4-C: + 2000 / 2014
  return state;
}

export function newRegionState() {
  return { securityMod: 0, incidents: [], partner: null, bribe: 0, insurance: null };
}

export function addRig(state, defId, name) {
  const uid = nextId(state, 'r');
  const n = Object.keys(state.rigs).length + 1;
  state.rigs[uid] = {
    uid, defId, name: name ?? `Rig ${n}`, condition: 100, engineUid: null, bitUid: null,
    line: null, at: null, wellId: null, crewIds: [], tours: 1, task: null,
    auto: { tripBelowPct: 15, dress: true, case: true, complete: true, drillThroughPay: true, rerope: true, straighten: true,
      // M2 rotary era (M2_SPEC §3, §5, §11)
      program: null, mudAuto: false, optimize: true, cement: true, fish: true },
    security: { guards: [], fort: null },
    safety: { budget: 0, upgrades: [], daysWithoutIncident: 0, boilerSpacingFt: 30 },
    gear: { pump: null, collars: null, capping: null }, bopUid: null, toolpusherId: null,
  };
  return state.rigs[uid];
}

export function addItem(state, defId, loc = { type: 'warehouse', id: null }) {
  const uid = nextId(state, 'i');
  state.items[uid] = { uid, defId, condition: 100, maxCondition: 100, loc };
  return state.items[uid];
}

export function addStock(state, defId, qty) {
  const s = (state.stock[defId] ??= { qty: 0, wear: 0 });
  s.qty += qty;
  return s;
}

export function takeStock(state, defId, qty) {
  const s = state.stock[defId];
  if (!s || s.qty < qty - 1e-9) return false;
  s.qty -= qty;
  if (s.qty <= 1e-9) delete state.stock[defId];
  return true;
}

export const stockQty = (state, defId) => state.stock[defId]?.qty ?? 0;

// ---- Field generation -------------------------------------------------------
// Per-field generator (M2_SPEC §0.2): field truth depends only on (seed, fieldId), so adding fields never
// shifts another field or the main state.rng stream, and migrated saves gain the same fields as a fresh game.
export function genField(state, fdef) {
  const g = subRng(state.seed ?? 0, `field:${fdef.id}`);
  return genFieldWith(g, fdef);
}

function genFieldWith(state, fdef) {
  const w = fdef.grid?.w ?? 6, h = fdef.grid?.h ?? 4;
  const seeps = new Set(fdef.seeps ?? []);
  const plots = [];
  for (let i = 0; i < w * h; i++) {
    const x = i % w, y = Math.floor(i / w);
    let prospectIdx = -1, dist = 0, edge = 1;
    (fdef.prospects ?? []).forEach((p, pi) => {
      if (prospectIdx >= 0) return;
      const e = Math.sqrt(((x - p.cx) / (p.rx || 1)) ** 2 + ((y - p.cy) / (p.ry || 1)) ** 2);
      const jitter = range(state, 0.85, 1.15);
      if (e * jitter <= 1) { prospectIdx = pi; dist = Math.hypot(x - p.cx, y - p.cy); edge = e; }
    });
    const depthMult = 1 + range(state, -0.05, 0.05);
    const isSeep = seeps.has(i);
    // Public knowledge: seeps are visible to everyone and hint at what's below.
    const pubEst = isSeep ? 0.7 : 0.3;
    plots.push(makePlot({
      x, y, seep: isSeep,
      truth: { prospectIdx, dist: round2(dist), edge: round2(edge), depthMult: round4(depthMult), qiRoll: round4(nextFloat(state)) },
      pubEst, priceMult: 1,
      owned: false, leaseDay: null, leaseExpires: null, survey: null, wellId: null,
      security: { guards: [], fort: null },
    }));
  }
  const reservoirs = {};
  (fdef.prospects ?? []).forEach((p) => {
    for (const r of p.reservoirs ?? []) {
      const ooip = r.ooip * Math.exp(normal(state, 0, 0.15));
      const initial = Math.round(ooip * (r.rf ?? 0.15));
      reservoirs[r.id] = { initial, remaining: initial, pressure: 1, shutInRegain: 0 };
    }
  });
  const tlist = fieldTransports(fdef);
  return {
    id: fdef.id, plots, reservoirs,
    storage: { capacityBbl: 0, bbl: 0, barrels: 0 },
    transportId: tlist[0]?.id && eqDef(tlist[0].id) ? tlist[0].id : (tlist[0]?.id ?? null),
    connected: [], shipQueue: 0, shipTimerId: null,
    sellMode: { mode: 'auto', pct: 100 }, boomFactor: 1, boomEvent: 0,
    storageFullWarned: false, netbackWarned: false, theftUntil: 0, theftPct: 0,
    regionId: fieldRegionId(fdef),
    security: { guards: [], fort: null },
  };
}

const round2 = (x) => Math.round(x * 100) / 100;
const round4 = (x) => Math.round(x * 10000) / 10000;
